import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { toSnakeCase } from './caseConverters'
import { useAuthStore } from '@/stores/authStore'

const baseURL = import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:8000/api/'

/**
 * Cliente Axios central:
 *   - converte payloads outgoing (POST/PUT/PATCH) para snake_case
 *   - injeta Authorization: Bearer <token> + X-User-ID
 *   - redireciona para /login em 401
 */
export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  // Autenticação é via Bearer token, não cookie — withCredentials desligado evita
  // CORS preflights desnecessários e Sanctum CSRF false-positives.
  withCredentials: false,
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken, user } = useAuthStore.getState()

  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`)
  }
  if (user?.id) {
    config.headers.set('X-User-ID', user.id)
  }

  // Não tocar em FormData (uploads multipart)
  if (config.data && !(config.data instanceof FormData) && typeof config.data === 'object') {
    config.data = toSnakeCase(config.data)
  }

  // Query params também convertidos
  if (config.params && typeof config.params === 'object') {
    config.params = toSnakeCase(config.params)
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const { clearAuth } = useAuthStore.getState()
      clearAuth()
      // Hard redirect — força React Router reler estado
      if (window.location.pathname !== '/login') {
        window.location.assign('/login')
      }
    }
    return Promise.reject(error)
  },
)
